import { useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface PaymentFormProps {
  onSuccess?: (paymentMethodId: string) => void;
  onError?: (error: string) => void;
  isSetupMode?: boolean;
  clientSecret?: string;
}

function PaymentFormInner({ onSuccess, onError, isSetupMode = false, clientSecret }: PaymentFormProps) {
  const stripe = useStripe();
  const { activeWorkgroupId } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRedirectToStripe = async () => {
    if (!stripe) {
      setError('Stripe has not been properly initialized');
      return;
    }

    if (!activeWorkgroupId) {
      setError('Workgroup not found');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      toast.info('Creating Stripe session...');

      const response = await api.billing.createCheckoutSession(
        `${window.location.origin}/billing`,
        `${window.location.origin}/billing`
      );

      if (response.data?.url) {
        toast.success('Redirecting to Stripe...');
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Payment setup failed. Please try again.';
      setError(errorMessage);
      onError?.(errorMessage);
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-4 border border-border rounded-lg bg-muted/50">
        <p className="text-sm text-muted-foreground mb-2">
          You will be redirected to Stripe's secure payment page to add your payment method.
        </p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <ExternalLink className="w-3 h-3" />
          <span>Powered by Stripe</span>
        </div>
      </div>

      {error && (
        <div className="text-red-600 text-sm p-2 bg-red-50 dark:bg-red-900/20 rounded-md">{error}</div>
      )}

      <Button
        type="button"
        onClick={handleRedirectToStripe}
        disabled={!stripe || isLoading}
        className="w-full h-10"
      >
        {isLoading ? 'Redirecting...' : 'Continue to Stripe'}
        <ExternalLink className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}

export function PaymentForm(props: PaymentFormProps) {
  return (
    <div>
      <PaymentFormInner {...props} />
    </div>
  );
}

interface PaymentMethodCardProps {
  paymentMethod: any;
  onRemove?: (paymentMethodId: string) => void;
  isDefault?: boolean;
}

export function PaymentMethodCard({ paymentMethod, onRemove, isDefault }: PaymentMethodCardProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleRemove = async () => {
    if (!onRemove) return;
    
    setIsLoading(true);
    try {
      await onRemove(paymentMethod.id);
      toast.success('Payment method removed successfully');
    } catch (error) {
      toast.error('Failed to remove payment method');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          <div className="text-2xl flex-shrink-0">
            {paymentMethod.type === 'card' ? '💳' : '🔗'}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">
              {paymentMethod.type === 'card'
                ? `${paymentMethod.card.brand} •••• ${paymentMethod.card.last4}`
                : paymentMethod.link.type
              }
            </div>
            {paymentMethod.type === 'card' && (
              <div className="text-sm text-gray-500">
                Expires {paymentMethod.card.exp_month}/{paymentMethod.card.exp_year}
              </div>
            )}
            {isDefault && (
              <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full inline-block mt-1">
                Default
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          {onRemove && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRemove}
              disabled={isLoading}
              className="whitespace-nowrap"
            >
              {isLoading ? 'Removing...' : 'Remove'}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}